import { AxiosRequestConfig } from "axios";
import merge from "lodash/merge";
import { GranularCatalogConfig } from "@granular/fabric-catalog-helpers";

import operationResource from "./v2/operation";
import operationIdResource from "./v2/operation/[operationId]";
import userResource from "./v2/operation/[operationId]/user";
import userIdResource from "./v2/operation/[operationId]/user/[userId]";
import corteva_userResource from "./v2/operation/[operationId]/corteva_user";
import settingResource from "./v2/operation/[operationId]/setting";

// v4
import operationIdResourceV4 from "./v4/operations/[operationId]";
import operationsResourceV4 from "./v4/operations/operations";

export type RolesSvcInstance = {
  getOperations: (
    params: Parameters<typeof operationResource.GET>[0],
    axiosRequestConfig?: AxiosRequestConfig,
  ) => ReturnType<typeof operationResource.GET>;
  getOperation: (
    operationId: Parameters<typeof operationIdResource.GET>[0],
    params: Parameters<typeof operationIdResource.GET>[1],
    axiosRequestConfig?: AxiosRequestConfig,
  ) => ReturnType<typeof operationIdResource.GET>;
  addOperation: (
    operation: Parameters<typeof operationResource.POST>[0],
    params: Parameters<typeof operationResource.POST>[1],
    axiosRequestConfig?: AxiosRequestConfig,
  ) => ReturnType<typeof operationResource.POST>;
  updateOperation: (
    operation: Parameters<typeof operationIdResource.PUT>[0],
    params: Parameters<typeof operationIdResource.PUT>[1],
    axiosRequestConfig?: AxiosRequestConfig,
  ) => ReturnType<typeof operationIdResource.PUT>;
  deleteOperation: (
    operationId: Parameters<typeof operationIdResource.DELETE>[0],
    axiosRequestConfig?: AxiosRequestConfig,
  ) => ReturnType<typeof operationIdResource.DELETE>;
  updateOperationSettings: (
    operationSettings: Parameters<typeof settingResource.PUT>[0],
    axiosRequestConfig?: AxiosRequestConfig,
  ) => ReturnType<typeof settingResource.PUT>;
  getUsers: (
    operationId: Parameters<typeof userResource.GET>[0],
    params: Parameters<typeof userResource.GET>[1],
    axiosRequestConfig?: AxiosRequestConfig,
  ) => ReturnType<typeof userResource.GET>;
  updateUsers: (
    operationId: Parameters<typeof userResource.PUT>[0],
    users: Parameters<typeof userResource.PUT>[1],
    axiosRequestConfig?: AxiosRequestConfig,
  ) => ReturnType<typeof userResource.PUT>;
  deleteUser: (
    operationId: Parameters<typeof userIdResource.DELETE>[0],
    userId: Parameters<typeof userIdResource.DELETE>[1],
    axiosRequestConfig?: AxiosRequestConfig,
  ) => ReturnType<typeof userIdResource.DELETE>;
  getCortevaUsers: (
    operationId: Parameters<typeof corteva_userResource.GET>[0],
    axiosRequestConfig?: AxiosRequestConfig,
  ) => ReturnType<typeof corteva_userResource.GET>;
  deleteCortevaUsers: (
    operationId: Parameters<typeof corteva_userResource.DELETE>[0],
    body: Parameters<typeof corteva_userResource.DELETE>[1],
    axiosRequestConfig?: AxiosRequestConfig,
  ) => ReturnType<typeof corteva_userResource.DELETE>;
  // v4
  // getOperationV4 is an alias for v4.getOperation
  getOperationV4: (
    operationId: Parameters<typeof operationIdResourceV4.GET>[0],
    axiosRequestConfig?: AxiosRequestConfig,
  ) => ReturnType<typeof operationIdResourceV4.GET>;
  v4: {
    getOperation: (
      operationId: Parameters<typeof operationIdResourceV4.GET>[0],
      axiosRequestConfig?: AxiosRequestConfig,
    ) => ReturnType<typeof operationIdResourceV4.GET>;
    getOperations: (
      params: Parameters<typeof operationsResourceV4.GET>[0],
      axiosRequestConfig?: AxiosRequestConfig,
    ) => ReturnType<typeof operationsResourceV4.GET>;
  };
};

const createRolesSvcInstance = (
  catalogConfig: GranularCatalogConfig,
  instanceRequestConfig?: AxiosRequestConfig,
): RolesSvcInstance => ({
  getOperations: (params, axiosRequestConfig) =>
    operationResource.GET(
      params,
      catalogConfig,
      merge(instanceRequestConfig, axiosRequestConfig),
    ),
  getOperation: (operationId, params, axiosRequestConfig) =>
    operationIdResource.GET(
      operationId,
      params,
      catalogConfig,
      merge(instanceRequestConfig, axiosRequestConfig),
    ),
  addOperation: (operation, params, axiosRequestConfig) =>
    operationResource.POST(
      operation,
      params,
      catalogConfig,
      merge(instanceRequestConfig, axiosRequestConfig),
    ),
  updateOperation: (operation, params, axiosRequestConfig) =>
    operationIdResource.PUT(
      operation,
      params,
      catalogConfig,
      merge(instanceRequestConfig, axiosRequestConfig),
    ),
  deleteOperation: (operationId, axiosRequestConfig) =>
    operationIdResource.DELETE(
      operationId,
      catalogConfig,
      merge(instanceRequestConfig, axiosRequestConfig),
    ),
  updateOperationSettings: (operationSettings, axiosRequestConfig) =>
    settingResource.PUT(
      operationSettings,
      catalogConfig,
      merge(instanceRequestConfig, axiosRequestConfig),
    ),
  getUsers: (operationId, params, axiosRequestConfig) =>
    userResource.GET(
      operationId,
      params,
      catalogConfig,
      merge(instanceRequestConfig, axiosRequestConfig),
    ),
  updateUsers: (operationId, users, axiosRequestConfig) =>
    userResource.PUT(
      operationId,
      users,
      catalogConfig,
      merge(instanceRequestConfig, axiosRequestConfig),
    ),
  deleteUser: (operationId, userId, axiosRequestConfig) =>
    userIdResource.DELETE(
      operationId,
      userId,
      catalogConfig,
      merge(instanceRequestConfig, axiosRequestConfig),
    ),
  getCortevaUsers: (operationId, axiosRequestConfig) =>
    corteva_userResource.GET(
      operationId,
      catalogConfig,
      merge(instanceRequestConfig, axiosRequestConfig),
    ),
  deleteCortevaUsers: (operationId, body, axiosRequestConfig) =>
    corteva_userResource.DELETE(
      operationId,
      body,
      catalogConfig,
      merge(instanceRequestConfig, axiosRequestConfig),
    ),
  // v4
  getOperationV4: (operationId, axiosRequestConfig) =>
    operationIdResourceV4.GET(
      operationId,
      catalogConfig,
      merge(instanceRequestConfig, axiosRequestConfig),
    ),
  v4: {
    getOperation: (operationId, axiosRequestConfig) =>
      operationIdResourceV4.GET(
        operationId,
        catalogConfig,
        merge(instanceRequestConfig, axiosRequestConfig),
      ),
    getOperations: (params, axiosRequestConfig) =>
      operationsResourceV4.GET(
        params,
        catalogConfig,
        merge(instanceRequestConfig, axiosRequestConfig),
      ),
  },
});

export default createRolesSvcInstance;
